import { useState, useEffect } from "react";
import { useTemplateStore } from "@/zustand/templateStore.js";
import { useProductData } from "@/zustand/productStore.js";
import { useCartData } from "@/zustand/cartStore.js";
import { useMarketingStore } from "@/zustand/emailMarketingStore.js";
import { ChevronDownIcon, QuestionMarkIcon } from "@/utils/miscIcons.jsx";
import { useOrderCalculations } from "@/func/orderCalculations.js";
import ProductsList from "@/components/productsList";
import DiscountInput from "@/components/inputs/DiscountInput";
import Separator from "@/components/misc/Separator";
import ExtendedWarrantyCheckbox from "@/components/orderSummary/ExtendedWarrantyCheckbox";
import TotalSavings from "@/components/orderSummary/TotalSavings";
import { isDiscountActive, isDiscountEnabledForSelection } from "@/utils/discounts";

const orderOverviewDrawerV3 = () => {
  const productData = useProductData();
  const { toggleOrderSummary, orderSummaryOpen, displayPopup } = useTemplateStore();
  const [warrantyEnabled, setWarrantyEnabled] = useState(null);
  const {
    selectedProduct,
    warrantyState,
    isDiscountApplied,
    setCartTotal,
    isSubscribe,
    discountCode,
    premiumShippingUpsellEnabled,
  } = useCartData();
  const summary = window.post_template?.checkout_v4_summary;
  const premiumShippingLabel =
    window.post_template?.checkout_v4_premium_shipping?.tr_checkout_v4_premium_shipping_summary_label ||
    "Premium Shipping";
  const shippingPolicyUrl = summary.checkout_v4_summary_shippingLink;
  const MultiplyWarranty = productData?.multiply_price_by_amount;
  const { freeShippingApplied } = useMarketingStore();
  const orderSummaryVersion = summary?.checkout_v4_summary_version;
  const hideTotalSavings = summary?.checkout_v4_summary_hide_total_savings;

  // function returns total, subtotal, shipping prices and total savings. props: pricingPackage, isDiscountApplied, MultiplyWarranty, warrantyState, formattedString
  const discountEnabledForSelection = isDiscountEnabledForSelection(productData, isSubscribe);
  const discountActive = isDiscountActive(productData, isSubscribe, discountCode, isDiscountApplied);

  const orderCalculations = useOrderCalculations(
    selectedProduct,
    discountActive,
    MultiplyWarranty,
    warrantyState,
    productData.formatted_string
  );

  useEffect(() => {
    // update Zustand cartTotal state
    setCartTotal(orderCalculations?.total_numeric);
  }, [orderCalculations?.total]);

  useEffect(() => {
    setWarrantyEnabled(productData?.add_warranty_for_product);
  }, [productData?.add_warranty_for_product]);

  const freeShippingLabel = freeShippingApplied
    ? summary.tr_checkout_v4_summary_free_shipping_applied_label
    : summary.tr_checkout_v4_summary_free_shipping_expired_label;

  return (
    <div className="orderOverviewDrawerV3">
      {orderSummaryVersion || hideTotalSavings ? null : (
        <div
          data-cy="cc-order-summary-container"
          className={`orderOverviewDrawerV3__topCont ${orderSummaryOpen && "orderOverviewDrawerV3__topCont__hide"}`}
        >
          <div className={`orderOverviewDrawerV3__savingsLabel__cont`}>
            <div className="orderOverviewDrawerV3__savingsLabel">{summary.tr_checkout_v4_summary_savingsLabel}</div>
            <div className="orderOverviewDrawerV3__savingsLabel">{orderCalculations?.savings}</div>
          </div>
          {freeShippingApplied && (
            <div className="orderOverviewDrawerV3__freeShippingLabelTop">
              {summary.tr_checkout_v4_summary_free_shipping_applied_label}
            </div>
          )}
          <Separator />
        </div>
      )}
      <div
        data-cy="cc-order-review-opener"
        className={`orderOverviewDrawerV3__opener ${orderSummaryOpen ? "_down" : ""} orderOverviewDrawerV3_label`}
        onClick={() => {
          toggleOrderSummary(!orderSummaryOpen);
        }}
        style={hideTotalSavings ? { display: "flex", justifyContent: "space-between", alignItems: "center" } : {}}
      >
        {orderSummaryVersion ? (
          <div className="summaryVersion2_drawer_title orderOverviewDrawerV3_label_title ">
            {window.post_template.checkout_v4_summary.tr_checkout_v4_order_review_accordion_title}
            {!hideTotalSavings && <ChevronDownIcon />}
          </div>
        ) : (
          <div className="orderOverviewDrawerV3_label_title">
            <span style={{ marginRight: "8px" }}>
              {window.post_template.checkout_v4_summary.tr_checkout_v4_order_review_accordion_title}
            </span>
            {!hideTotalSavings && <ChevronDownIcon />}
          </div>
        )}
        {hideTotalSavings && <ChevronDownIcon />}
        {orderSummaryVersion || hideTotalSavings ? null : (
          <div className="orderOverviewDrawerV3_label_priceContainer">
            <div className="orderOverviewDrawerV3_label_price">{orderCalculations?.total}</div>
          </div>
        )}
      </div>

      <div
        style={displayPopup ? { overflow: "visible" } : {}}
        className={`orderOverviewDrawerV3__content ${orderSummaryOpen && "orderOverviewDrawerV3__content__show"}`}
      >
        <Separator margin="16px 0px 16px 0px" />
        <ProductsList />
        {warrantyEnabled && (
          <>
            <Separator margin="16px 0px 16px 0px" />
            <ExtendedWarrantyCheckbox />
          </>
        )}
        <Separator margin="16px 0px 0px 0px" />
        {discountEnabledForSelection && (
          <>
            <DiscountInput /> <Separator margin="0px 0px 0px 0px" />
          </>
        )}
        <div className="orderTotals__container" style={{ marginBottom: "0px" }}>
          <div className="orderTotals">
            <div className="orderTotals__item">
              <p>{window.post_template?.checkout_v4_summary.tr_checkout_v4_summary_orderLabel}</p>
              <p data-cy="summary-subtotal">{orderCalculations?.subtotal}</p>
            </div>
            <div className="orderTotals__item">
              <p data-testid="summary-shipping-label" className="orderTotals__item__shippingLabel">
                <span style={{ marginRight: "4px" }}>
                  {premiumShippingUpsellEnabled
                    ? premiumShippingLabel
                    : window.post_template.checkout_v4_summary.tr_checkout_v4_summary_shippingLabel}
                </span>{" "}
                <a className="orderTotals__item__shippingLabel--link" href={shippingPolicyUrl}>
                  <QuestionMarkIcon />
                </a>
              </p>
              <p data-cy="summary-shipping">{orderCalculations?.shipping}</p>
            </div>
            {freeShippingApplied && (
              <div
                className={`orderOverviewDrawerV3__freeShippingLabelBottom ${!freeShippingApplied ? "expired" : ""}`}
              >
                {freeShippingLabel}
              </div>
            )}
          </div>
          <Separator />
          <TotalSavings
            type={window.post_template?.totalSavingsStyle}
            label={window.post_template?.checkout_v4_summary?.tr_checkout_v4_summary_savingsLabel}
            savings={orderCalculations?.savings}
          />
          <div className="total">
            <div>{window.post_template?.checkout_v4_summary.tr_checkout_v4_summary_totalLabel}</div>
            <div data-cy="summary-total">{orderCalculations?.total}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default orderOverviewDrawerV3;
